import React from 'react';

const TicketEditModal = ({
  ticket,
  onClose,
  assignableUsers = [],
  handleUpdateTicket,
  isLoading,
  setTicket,
}) => {
  if (!ticket) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="fixed inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800 animate-fadeIn z-10">
        
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 bg-blue-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shadow-2xs flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-white tracking-tight">Edit Ticket</h3>
              <p className="text-[10px] text-blue-100 font-mono">
                #{ticket.id?.slice(0, 8)} • Update Properties
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 text-xs">
          
          {/* Target Ticket Context Card */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
              Ticket Details
            </span>
            <div className="font-bold text-slate-900 text-xs sm:text-sm line-clamp-1">{ticket.title}</div>
            <div className="text-[10px] text-slate-500">
              Created by <strong className="text-slate-700">{ticket.created_by_username || 'User'}</strong>
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Ticket Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTicket({ ...ticket, status: 'open' })}
                className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  ticket.status === 'open'
                    ? 'bg-blue-50 border-blue-300 text-blue-900 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span>Open</span>
              </button>

              <button
                type="button"
                onClick={() => setTicket({ ...ticket, status: 'in_progress' })}
                className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  ticket.status === 'in_progress'
                    ? 'bg-amber-50 border-amber-300 text-amber-900 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                <span>In Progress</span>
              </button>
            </div>
          </div>

          {/* Priority Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Priority Level
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'low', label: 'Low', color: 'bg-emerald-500', activeClass: 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-2xs' },
                { id: 'medium', label: 'Medium', color: 'bg-amber-500', activeClass: 'bg-amber-50 border-amber-300 text-amber-900 shadow-2xs' },
                { id: 'high', label: 'High', color: 'bg-rose-500', activeClass: 'bg-rose-50 border-rose-300 text-rose-900 shadow-2xs' }
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setTicket({ ...ticket, priority: p.id })}
                  className={`p-2.5 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    ticket.priority === p.id
                      ? p.activeClass
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${p.color}`}></span>
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Assignee Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Assigned Agent
            </label>
            <div className="relative">
              <select
                className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-500 rounded-xl p-2.5 text-xs text-slate-800 focus:outline-none transition-all font-medium"
                value={ticket.assigned_to || ''}
                onChange={(e) => setTicket({ ...ticket, assigned_to: e.target.value || null })}
              >
                <option value="">Unassigned (Queue Pool)</option>
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                handleUpdateTicket(ticket.id, {
                  status: ticket.status,
                  priority: ticket.priority,
                  assigned_to: ticket.assigned_to,
                })
              }
              disabled={isLoading}
              className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-xs disabled:opacity-50 transition-all flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};

export default TicketEditModal;
